import { createActionCreator } from '@pressreader/appstore';
import type { CollectionWithId, IssuePageSet } from '@pressreader/social-types';

import { MODULE_NAME } from '../../constants';

const pre = MODULE_NAME + '/issue/pageset/';

interface WithIssueKey {
    issueKey: string;
}

interface WithPageSetId {
    pageSetId: string;
}

interface LoadPageSetByIdActionPayload extends WithPageSetId {
    /**
     * @default false
     */
    isForce?: boolean;
}

interface LoadIssuePageSetDonePayload extends WithIssueKey {
    pageSet: IssuePageSet[];
}

interface LoadIssuePageSetByIssueKeyFailedPayload extends WithIssueKey {
    error: unknown;
}

interface LoadIssuePageSetByIdFailedPayload extends WithPageSetId {
    error: unknown;
}

const loadIssuePageSetByIssueKeyAction = createActionCreator<WithIssueKey>(`${pre}load_issue_pageset_by_issue_key_action`);
const loadIssuePageSetByIssueKeyBegan = createActionCreator<WithIssueKey>(`${pre}load_issue_pageset_by_issue_key_began`);
const loadIssuePageSetByIssueKeyDone = createActionCreator<LoadIssuePageSetDonePayload>(`${pre}load_issue_pageset_by_issue_key_done`);
const loadIssuePageSetByIssueKeyFailed = createActionCreator<LoadIssuePageSetByIssueKeyFailedPayload>(`${pre}load_issue_pageset_by_issue_key_failed`);
const loadIssuePageSetByIssueKeyCancelled = createActionCreator<WithIssueKey>(`${pre}load_issue_pageset_by_issue_key_cancelled`);
const resetIssuePageSetByIssueKeyAction = createActionCreator<WithIssueKey>(`${pre}reset_issue_pageset`);

const loadIssuePageSetByIdAction = createActionCreator<LoadPageSetByIdActionPayload>(`${pre}load_issue_pageset_by_id_action`);
const loadIssuePageSetByIdBegan = createActionCreator<WithPageSetId>(`${pre}load_issue_pageset_by_id_began`);
const loadIssuePageSetByIdDone = createActionCreator<LoadIssuePageSetDonePayload>(`${pre}load_issue_pageset_by_issue_key_done`);
const loadIssuePageSetByIdFailed = createActionCreator<LoadIssuePageSetByIdFailedPayload>(`${pre}load_issue_pageset_by_id_failed`);
const loadIssuePageSetByIdCancelled = createActionCreator<WithPageSetId>(`${pre}load_issue_pageset_by_id_cancelled`);
const resetIssuePageSetByIdAction = createActionCreator<WithPageSetId>(`${pre}reset_issue_pageset_by_id`);

interface UpsertIssuePageSetCollectionPayload extends WithPageSetId {
    collections: CollectionWithId[];
}
const upsertIssuePageSetCollectionAction = createActionCreator<UpsertIssuePageSetCollectionPayload>(`${pre}upsert_page_set_collection_action`);
const upsertIssuePageSetCollectionBegan = createActionCreator<UpsertIssuePageSetCollectionPayload>(`${pre}upsert_page_set_collection_began`);
const upsertIssuePageSetCollectionDone = createActionCreator<UpsertIssuePageSetCollectionPayload>(`${pre}upsert_page_set_collection_done`);
const upsertIssuePageSetCollectionFailed = createActionCreator<UpsertIssuePageSetCollectionPayload>(`${pre}upsert_page_set_collection_failed`);
const upsertIssuePageSetCollectionFinishedActionList = Object.freeze([upsertIssuePageSetCollectionDone, upsertIssuePageSetCollectionFailed]);

const removePageSetFromCollectionsAction = createActionCreator<WithPageSetId>(`${pre}remove_pageset_from_collections_action`);
const removePageSetFromCollectionsBegan = createActionCreator<WithPageSetId>(`${pre}remove_pageset_from_collections_began`);
const removePageSetFromCollectionsDone = createActionCreator<WithPageSetId>(`${pre}remove_pageset_from_collections_done`);
const removePageSetFromCollectionsFailed = createActionCreator<WithPageSetId>(`${pre}remove_pageset_from_collections_failed`);

interface UpdateIssuePageSetByIdActionPayload extends WithPageSetId {
    pages: number[];
}
const updateIssuePageSetByIdAction = createActionCreator<UpdateIssuePageSetByIdActionPayload>(`${pre}update_issue_pageset_by_id_action`);
const updateIssuePageSetByIdBegan = createActionCreator<WithPageSetId>(`${pre}update_issue_pageset_by_id_began`);
const updateIssuePageSetByIdDone = createActionCreator<UpdateIssuePageSetByIdActionPayload>(`${pre}update_issue_pageset_by_issue_key_done`);
const updateIssuePageSetByIdFailed = createActionCreator<WithPageSetId>(`${pre}update_issue_pageset_by_id_failed`);

const loadPageSetFinishedActionList = Object.freeze([
    loadIssuePageSetByIssueKeyDone,
    loadIssuePageSetByIssueKeyFailed,
    loadIssuePageSetByIssueKeyCancelled,
    loadIssuePageSetByIdDone,
    loadIssuePageSetByIdFailed,
    loadIssuePageSetByIdCancelled,
    removePageSetFromCollectionsDone,
    removePageSetFromCollectionsFailed,
    updateIssuePageSetByIdDone,
    updateIssuePageSetByIdFailed,
    ...upsertIssuePageSetCollectionFinishedActionList,
]);

export type { LoadIssuePageSetDonePayload, WithPageSetId, UpdateIssuePageSetByIdActionPayload };
export {
    loadIssuePageSetByIssueKeyAction,
    loadIssuePageSetByIssueKeyBegan,
    loadIssuePageSetByIssueKeyDone,
    loadIssuePageSetByIssueKeyFailed,
    loadIssuePageSetByIssueKeyCancelled,
    resetIssuePageSetByIssueKeyAction,
    loadIssuePageSetByIdAction,
    loadIssuePageSetByIdBegan,
    loadIssuePageSetByIdDone,
    loadIssuePageSetByIdFailed,
    loadIssuePageSetByIdCancelled,
    loadPageSetFinishedActionList,
    resetIssuePageSetByIdAction,
    upsertIssuePageSetCollectionAction,
    upsertIssuePageSetCollectionBegan,
    upsertIssuePageSetCollectionDone,
    upsertIssuePageSetCollectionFailed,
    upsertIssuePageSetCollectionFinishedActionList,
    removePageSetFromCollectionsAction,
    removePageSetFromCollectionsBegan,
    removePageSetFromCollectionsDone,
    removePageSetFromCollectionsFailed,
    updateIssuePageSetByIdAction,
    updateIssuePageSetByIdBegan,
    updateIssuePageSetByIdDone,
    updateIssuePageSetByIdFailed,
};
